<template>
  <div class="g-map-wrapper">
    <!-- Wrapper qui englobe le canvas Mapbox + le sélecteur de style, pour que le sélecteur
         reste toujours collé au bas de la carte quelle que soit la disposition (split/mini/full) -->
    <div class="map-canvas-wrapper" :style="swapped && (livestreamOthersVisible || livestreamAgoraVisible)
      ? { position: 'absolute', bottom: '10px', left: '10px', width: '25vw', height: 'calc(25vw * 3 / 4)', zIndex: 10, borderRadius: '4px', overflow: 'hidden', border: '2px solid #444', cursor: 'zoom-out' }
      : { width: '100%', height: '100%' }"
      @click="swapped && (livestreamOthersVisible || livestreamAgoraVisible) ? store.commit('SET_LIVESTREAM_SWAPPED', false) : null">
      <!-- 地图区域 -->
      <div id="g-container" style="width:100%; height:100%;" />
      <!-- Aide au dessin : visible uniquement pendant le tracé d'une ligne ou d'un polygone -->
      <div
        v-if="!isMini && mouseMode && (state.currentType === 'polyline' || state.currentType === 'polygon')"
        class="draw-hint-banner"
      >
        Double-cliquer lors de l'ajout du dernier point de la forme
      </div>
      <!-- Confirmation : photo placée sur la carte (depuis le panneau Média) -->
      <div v-if="mediaPinDraft" class="media-pin-confirm">
        <div class="media-pin-confirm__label">
          <span class="media-pin-confirm__name" :title="mediaPinDraft.name">📍 {{ mediaPinDraft.name }}</span>
          <span class="media-pin-confirm__alt">
            {{ mediaPinDraft.height > 0 ? `${mediaPinDraft.height} m au-dessus du sol` : 'au sol' }}
          </span>
        </div>
        <button class="media-pin-confirm__btn media-pin-confirm__btn--save" :disabled="mediaPinSaving" @click="saveMediaPin">
          {{ mediaPinSaving ? 'Enregistrement…' : 'Sauvegarder' }}
        </button>
        <button class="media-pin-confirm__btn" :disabled="mediaPinSaving" @click="removeMediaPinDraft">
          Annuler
        </button>
      </div>
      <!-- Sélecteur de style de carte — positionné par rapport au canvas -->
      <div v-if="!isMini" class="map-style-switcher">
        <a-select :value="currentMapStyle" size="small" style="width: 150px;" @change="changeMapStyle">
          <a-select-option value="satellite-streets-v12">Satellite + routes</a-select-option>
          <a-select-option value="satellite-v9">Satellite</a-select-option>
          <a-select-option value="streets-v12">Plan</a-select-option>
          <a-select-option value="outdoors-v12">Randonnée</a-select-option>
          <a-select-option value="light-v11">Clair</a-select-option>
          <a-select-option value="dark-v11">Sombre</a-select-option>
        </a-select>
        <a-button
          size="small"
          :type="is3D ? 'primary' : 'default'"
          style="margin-left: 6px;"
          @click="toggle3D"
        >3D</a-button>
        <!-- <a-tooltip title="Terrain 3D haute résolution IGN (RGE ALTI / LiDAR HD) — France métropolitaine">
          <a-button
            size="small"
            :type="terrainIgn ? 'primary' : 'default'"
            style="margin-left: 6px;"
            @click="toggleTerrainIgn"
          >LiDAR 3D</a-button>
        </a-tooltip> -->
        <a-tooltip title="Afficher / masquer les bâtiments en 3D (styles Satellite+routes, Plan, Randonnée — visibles caméra inclinée)">
          <a-button
            size="small"
            :type="buildings3D ? 'primary' : 'default'"
            style="margin-left: 6px;"
            @click="toggleBuildings3D"
          >Bâtiments 3D</a-button>
        </a-tooltip>
        <a-tooltip title="Unité de la vitesse du vent (m/s ⇆ km/h) — s'applique à toute l'interface">
          <a-button
            size="small"
            style="margin-left: 6px;"
            @click="toggleWindUnit"
          >Vent : {{ windUnitLabel() }}</a-button>
        </a-tooltip>
        <div v-if="is3D" class="terrain-exag">
          <span class="terrain-exag-label">Relief</span>
          <a-slider
            v-model:value="terrainExag"
            :min="1"
            :max="3"
            :step="0.5"
            :tip-formatter="(v) => `×${v}`"
            style="width: 90px; margin: 0 0 0 6px;"
            @change="onExagChange"
          />
        </div>
      </div>
    </div>
    <!-- 绘制面板 -->
    <div
      v-if="canAddAnnotations && !isMini"
      class="g-action-panel"
      :style="{ right: drawVisible ? '316px' : '16px' }"
    >
      <div :class="state.currentType === 'pin' ? 'g-action-item selection' : 'g-action-item'" @click="draw('pin', true)">
        <a><a-image :src="pin" :preview="false" /></a>
      </div>
      <div :class="state.currentType === 'polyline' ? 'g-action-item selection' : 'g-action-item'" @click="draw('polyline', true)">
        <a><LineOutlined :rotate="135" class="fz20"/></a>
      </div>
      <div :class="state.currentType === 'polygon' && !state.isFlightArea ? 'g-action-item selection' : 'g-action-item'" @click="draw('polygon', true)">
        <a><BorderOutlined class="fz18" /></a>
      </div>
      <div :class="state.currentType === 'circle' && !state.isFlightArea ? 'g-action-item selection' : 'g-action-item'" @click="draw('circle', true)">
        <a style="font-size:18px; line-height:1;">◎</a>
      </div>
      <!-- Bouton zones (flight/task area) masqué à la demande -->
      <FlightAreaActionIcon v-if="false" class="g-action-item mt10" :class="{'selection': mouseMode && state.isFlightArea}" @select-action="selectFlightAreaAction" @click="selectFlightAreaAction"/>
      <div v-if="mouseMode" class="g-action-item" @click="draw('off', false)">
        <a style="color: red;"><CloseOutlined /></a>
      </div>
    </div>
    <!-- Fonds de carte additionnels (overlays IGN) -->
    <MapOverlaysPanel v-if="!isMini" />
    <!-- Drone -->
    <div v-if="!isMini && osdVisible.visible && !osdVisible.is_dock" v-drag-window class="osd-panel fz12" :style="osdPositionStyle">
      <div class="drag-title pl5 pr5 flex-align-center flex-row flex-justify-between" style="border-bottom: 1px solid #515151; height: 18%;">
        <span>{{ osdVisible.callsign }}</span>
        <span><a class="fz16" style="color: white;" @click="() => osdVisible.visible = false"><CloseOutlined /></a></span>
      </div>
      <div style="height: 82%;">
        <div class="flex-column flex-align-center flex-justify-center" style="margin-top: -5px; padding-top: 25px; float: left; width: 60px; background: #2d2d2d;">
          <a-tooltip :title="osdVisible.model">
            <div style="width: 90%;" class="flex-column flex-align-center flex-justify-center">
              <span><a-image :src="`${osdVisible.model === 'Mavic 3E' ? Mavic3E : M30 }`" :preview="false"/></span>
              <span>{{ osdVisible.model }}</span>
            </div>
          </a-tooltip>
        </div>
        <div class="osd">
            <a-row>
              <a-col span="16" :style="deviceInfo.device.mode_code === EModeCode.Disconnected ? 'color: red; font-weight: 700;': 'color: rgb(25,190,107)'">{{ EModeCode[deviceInfo.device.mode_code] }}</a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Signal strength">
                  <span>HD</span>
                  <span class="ml10">{{ deviceInfo.gateway?.transmission_signal_quality }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="RC Battery Level">
                  <span><ThunderboltOutlined class="fz14"/></span>
                  <span v-if="deviceInfo.gateway?.charge_state === 1" title="En charge">⚡</span>
                  <span class="ml10">{{ deviceInfo.gateway && deviceInfo.gateway.capacity_percent !== str ? deviceInfo.gateway?.capacity_percent + ' %' : deviceInfo.gateway?.capacity_percent }}</span>
                </a-tooltip>
              </a-col>

              <a-col span="6">
                <a-tooltip title="Drone Battery Level">
                  <span><ThunderboltOutlined class="fz14"/></span>
                  <span v-if="deviceInfo.device.battery?.charge_state === 1" title="En charge">⚡</span>
                  <span class="ml10">{{ deviceInfo.device.battery.capacity_percent !== str ? deviceInfo.device.battery.capacity_percent + ' %' : deviceInfo.device.battery.capacity_percent }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-tooltip title="RTK Fixed">
                <a-col span="6" class="flex-row flex-align-center flex-justify-start">
                  <span>Fixed</span>
                  <span class="ml10 circle" :style="deviceInfo.device.position_state.is_fixed === 1 ? 'backgroud: rgb(25,190,107);' : ' background: red;'"></span>
                </a-col>
              </a-tooltip>
              <a-col span="6">
                <a-tooltip title="GPS">
                  <span>GPS</span>
                  <span class="ml10">{{ deviceInfo.device.position_state.gps_number }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="RTK">
                  <span><TrademarkOutlined class="fz14"/></span>
                  <span class="ml10">{{ deviceInfo.device.position_state.rtk_number }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Flight Mode">
                  <span><ControlOutlined class="fz16" /></span>
                  <span class="ml10">{{ EGear[deviceInfo.device.gear] }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Altitude above sea level (MSL, EGM96)">
                  <span>ASL</span>
                  <span class="ml10">{{ deviceInfo.device.height === str ? str : applyGeoidCorrection(deviceInfo.device.height).toFixed(1) + ' m'}}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Altitude above takeoff level">
                  <span>ALT</span>
                  <span class="ml10">{{ deviceInfo.device.elevation === str ? str : deviceInfo.device.elevation.toFixed(2) + ' m' }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Distance to Home Point">
                  <span>H</span>
                  <span class="ml10">{{ deviceInfo.device.home_distance === str ? str : deviceInfo.device.home_distance.toFixed(2) + ' m' }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Horizontal Speed">
                  <span>H.S</span>
                  <span class="ml10">{{ deviceInfo.device.horizontal_speed === str ? str : deviceInfo.device.horizontal_speed.toFixed(2) + ' m/s'}}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Vertical Speed">
                  <span>V.S</span>
                  <span class="ml10">{{ deviceInfo.device.vertical_speed === str ? str : deviceInfo.device.vertical_speed.toFixed(2) + ' m/s'}}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Wind Speed">
                  <span>W.S</span>
                  <span class="ml10">{{ deviceInfo.device.wind_speed === str ? str : formatWindSpeed(deviceInfo.device.wind_speed / 10, 2) }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
        </div>
      </div>
      <div class="battery-slide" v-if="deviceInfo.device.battery.remain_flight_time !== 0">
        <div style="background: #535759;" class="width-100"></div>
        <div class="capacity-percent" :style="{ width: deviceInfo.device.battery.capacity_percent + '%'}"></div>
        <div class="return-home" :style="{ width: deviceInfo.device.battery.return_home_power + '%'}"></div>
        <div class="landing" :style="{ width: deviceInfo.device.battery.landing_power + '%'}"></div>
        <div class="white-point" :style="{ left: deviceInfo.device.battery.landing_power + '%'}"></div>
        <div class="battery" :style="{ left: deviceInfo.device.battery.capacity_percent + '%' }">
          {{ Math.floor(deviceInfo.device.battery.remain_flight_time / 60) }}:
          {{ 10 > (deviceInfo.device.battery.remain_flight_time % 60) ? '0' : ''}}{{deviceInfo.device.battery.remain_flight_time % 60 }}
        </div>
      </div>
    </div>
    <!-- Dock -->
    <div v-if="!isMini && osdVisible.visible && osdVisible.is_dock"  v-drag-window class="osd-panel fz12" :style="osdPositionStyle">
      <div class="drag-title fz16 pl5 pr5 flex-align-center flex-row flex-justify-between" style="border-bottom: 1px solid #515151; height: 10%;">
        <span>{{ osdVisible.gateway_callsign }}</span>
      </div>
      <span><a style="color: white; position: absolute; top: 5px; right: 5px;" @click="() => osdVisible.visible = false"><CloseOutlined /></a></span>
        <!-- Dock -->
      <div class ="flex-display" style="border-bottom: 1px solid #515151;">
        <div class="flex-column flex-align-stretch flex-justify-center" style="width: 60px; background: #2d2d2d;">
          <a-tooltip :title="osdVisible.model">
            <div class="flex-column  flex-align-center flex-justify-center" style="width: 90%;">
              <span><RobotFilled style="font-size: 48px;"/></span>
              <span class="mt10">Dock</span>
            </div>
          </a-tooltip>
        </div>
        <div class="osd flex-1" style="flex: 1">
            <a-row>
              <a-col span="16" :style="deviceInfo.dock.basic_osd?.mode_code === EDockModeCode.Disconnected ? 'color: red; font-weight: 700;': 'color: rgb(25,190,107)'">
                {{ EDockModeCode[deviceInfo.dock.basic_osd?.mode_code] }}</a-col>
            </a-row>
            <a-row>
              <a-col span="12">
                <a-tooltip title="Accumulated Running Time">
                  <span><HistoryOutlined /></span>
                  <span class="ml10">
                    <span v-if="deviceInfo.dock.work_osd?.acc_time >= 2592000"> {{ Math.floor(deviceInfo.dock.work_osd?.acc_time / 2592000) }}m </span>
                    <span v-if="(deviceInfo.dock.work_osd?.acc_time % 2592000) >= 86400"> {{ Math.floor((deviceInfo.dock.work_osd?.acc_time % 2592000) / 86400) }}d </span>
                    <span v-if="(deviceInfo.dock.work_osd?.acc_time % 2592000 % 86400) >= 3600"> {{ Math.floor((deviceInfo.dock.work_osd?.acc_time % 2592000 % 86400) / 3600) }}h </span>
                    <span v-if="(deviceInfo.dock.work_osd?.acc_time % 2592000 % 86400 % 3600) >= 60"> {{ Math.floor((deviceInfo.dock.work_osd?.acc_time % 2592000 % 86400 % 3600) / 60) }}min </span>
                    <span>{{ Math.floor(deviceInfo.dock.work_osd?.acc_time % 2592000 % 86400 % 3600 % 60) }} s</span>
                  </span>
                </a-tooltip>
              </a-col>
              <a-col span="12">
                <a-tooltip title="Activation time">
                  <span><FieldTimeOutlined /></span>
                  <span class="ml10">{{ new Date((deviceInfo.dock.work_osd?.activation_time ?? 0) * 1000).toLocaleString() }}
                  </span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Network State">
                  <span :style="qualityStyle">
                    <span v-if="deviceInfo.dock.basic_osd?.network_state?.type === NetworkStateTypeEnum.FOUR_G"><SignalFilled /></span>
                    <span v-else><GlobalOutlined /></span>
                  </span>
                  <span class="ml10" >{{ deviceInfo.dock.basic_osd?.network_state?.rate }} kb/s</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="The total number of times the dock has performed missions.">
                  <span><CarryOutOutlined /></span>
                  <span class="ml10" >{{ deviceInfo.dock.work_osd?.job_number }} </span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Media File Remain Upload">
                  <span><CloudUploadOutlined class="fz14"/></span>
                  <span class="ml10">{{ deviceInfo.dock.link_osd?.media_file_detail?.remain_upload }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip>
                  <template #title>
                    <p>total: {{ deviceInfo.dock.basic_osd?.storage?.total }}</p>
                    <p>used: {{ deviceInfo.dock.basic_osd?.storage?.used  }}</p>
                  </template>
                  <span><FolderOpenOutlined /></span>
                  <span class="ml10" v-if="deviceInfo.dock.basic_osd?.storage?.total > 0">
                    <a-progress type="circle" :width="20" :percent="deviceInfo.dock.basic_osd?.storage?.used * 100/ deviceInfo.dock.basic_osd?.storage?.total"
                      :strokeWidth="20" :showInfo="false" :strokeColor="deviceInfo.dock.basic_osd?.storage?.used * 100 / deviceInfo.dock.basic_osd?.storage?.total > 80 ? 'red' : '#00ee8b' "/>
                  </span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Wind Speed">
                  <span>W.S</span>
                  <span class="ml10">{{ deviceInfo.dock.basic_osd?.wind_speed == null ? str : formatWindSpeed(deviceInfo.dock.basic_osd.wind_speed, 2) }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Rainfall">
                  <span>🌧</span>
                  <span class="ml10">{{ RainfallEnum[deviceInfo.dock.basic_osd?.rainfall] }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Environment Temperature">
                  <span>°C</span>
                  <span class="ml10">{{ deviceInfo.dock.basic_osd?.environment_temperature }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Dock Temperature">
                  <span>°C</span>
                  <span class="ml10">{{ deviceInfo.dock.basic_osd?.temperature }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Dock Humidity">
                  <span>💦</span>
                  <span class="ml10">{{ deviceInfo.dock.basic_osd?.humidity }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Working Voltage">
                  <span style="border: 1px solid; border-radius: 50%; width: 18px; height: 18px; line-height: 16px; text-align: center; float: left;">V</span>
                  <span class="ml10">{{ (deviceInfo.dock.work_osd?.working_voltage ?? str) + ' mV' }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Working Current">
                  <span style="border: 1px solid; border-radius: 50%; width: 18px; height: 18px; line-height: 15px; text-align: center; float: left;" >A</span>
                  <span class="ml10">{{ (deviceInfo.dock.work_osd?.working_current ?? str) + ' mA' }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Drone in dock">
                  <span><RocketOutlined /></span>
                  <span class="ml10">{{ deviceInfo.dock.basic_osd?.drone_in_dock }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row class="p5">
              <a-col span="24">
                <a-button type="primary" :disabled="dockControlPanelVisible" size="small" @click="setDockControlPanelVisible(true)">
                  Actions
                </a-button>
              </a-col>
            </a-row>
            <!-- 机场控制面板 -->
            <DockControlPanel v-if="dockControlPanelVisible" :sn="osdVisible.gateway_sn"  :deviceInfo="deviceInfo" @close-control-panel="onCloseControlPanel">
            </DockControlPanel>
        </div>
      </div>
      <!--  飞机-->
      <div class ="flex-display">
        <div class="flex-column flex-align-stretch flex-justify-center" style="width: 60px;  background: #2d2d2d;">
          <a-tooltip :title="osdVisible.model">
            <div style="width: 90%;" class="flex-column flex-align-center flex-justify-center">
              <span><a-image :src="osdVisible.model" :preview="false"/></span>
              <span>{{ osdVisible.model }}</span>
            </div>
          </a-tooltip>
        </div>
        <div class="osd flex-1">
            <a-row>
              <a-col span="16" :style="!deviceInfo.device || deviceInfo.device?.mode_code === EModeCode.Disconnected ? 'color: red; font-weight: 700;': 'color: rgb(25,190,107)'">
                {{ !deviceInfo.device ? EModeCode[EModeCode.Disconnected] : EModeCode[deviceInfo.device?.mode_code] }}</a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Upward Quality">
                  <span><SignalFilled /><ArrowUpOutlined style="font-size: 9px; vertical-align: top;" /></span>
                  <span class="ml10">{{ deviceInfo.dock.link_osd?.sdr?.up_quality }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Downward Quality">
                  <span><SignalFilled /><ArrowDownOutlined style="font-size: 9px; vertical-align: top;" /></span>
                  <span class="ml10">{{ deviceInfo.dock.link_osd?.sdr?.down_quality }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Drone Battery Level">
                  <span><ThunderboltOutlined class="fz14"/></span>
                  <span v-if="deviceInfo.dock.basic_osd?.drone_charge_state?.state === 1" title="En charge">⚡</span>
                  <span class="ml10">{{ deviceInfo.device && deviceInfo.device.battery.capacity_percent !== str ? deviceInfo.device?.battery.capacity_percent + ' %' : str }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip>
                  <template #title>
                    <p>total: {{ deviceInfo.device?.storage?.total }}</p>
                    <p>used: {{ deviceInfo.device?.storage?.used  }}</p>
                  </template>
                  <span><FolderOpenOutlined /></span>
                  <span class="ml10" v-if="deviceInfo.device?.storage?.total > 0">
                    <a-progress type="circle" :width="20" :percent="deviceInfo.device?.storage?.used * 100/ deviceInfo.device?.storage?.total"
                      :strokeWidth="20" :showInfo="false" :strokeColor="deviceInfo.device?.storage?.used * 100 / deviceInfo.device?.storage?.total > 80 ? 'red' : '#00ee8b' "/>
                  </span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-tooltip title="RTK Fixed">
                <a-col span="6" class="flex-row flex-align-center flex-justify-start">
                  <span>Fixed</span>
                  <span class="ml10 circle" :style="deviceInfo.device?.position_state.is_fixed === 1 ? 'backgroud: rgb(25,190,107);' : ' background: red;'"></span>
                </a-col>
              </a-tooltip>
              <a-col span="6">
                <a-tooltip title="GPS">
                  <span>GPS</span>
                  <span class="ml10">{{ deviceInfo.device ? deviceInfo.device.position_state.gps_number : str }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="RTK">
                  <span><TrademarkOutlined class="fz14"/></span>
                  <span class="ml10">{{ deviceInfo.device ? deviceInfo.device.position_state.rtk_number : str }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Flight Mode">
                  <span><ControlOutlined class="fz16" /></span>
                  <span class="ml10">{{ deviceInfo.device ? EGear[deviceInfo.device?.gear] : str }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Altitude above sea level (MSL, EGM96)">
                  <span>ASL</span>
                  <span class="ml10">{{ !deviceInfo.device || deviceInfo.device.height === str ? str : applyGeoidCorrection(deviceInfo.device.height).toFixed(1) + ' m'}}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Altitude above takeoff level">
                  <span>ALT</span>
                  <span class="ml10">{{ !deviceInfo.device || deviceInfo.device.elevation === str ? str : deviceInfo.device?.elevation.toFixed(2) + ' m' }}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Distance to Home Point">
                  <span style="border: 1px solid; border-radius: 50%; width: 18px; height: 18px; line-height: 15px; text-align: center;  display: block; float: left;" >D</span>
                  <span class="ml10">{{ !deviceInfo.device || deviceInfo.device.home_distance === str ? str : deviceInfo.device?.home_distance.toFixed(2) + ' m' }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-row>
              <a-col span="6">
                <a-tooltip title="Horizontal Speed">
                  <span>H.S</span>
                  <span class="ml10">{{ !deviceInfo.device || deviceInfo.device?.horizontal_speed === str ? str : deviceInfo.device?.horizontal_speed.toFixed(2) + ' m/s'}}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Vertical Speed">
                  <span>V.S</span>
                  <span class="ml10">{{ !deviceInfo.device || deviceInfo.device.vertical_speed === str ? str : deviceInfo.device?.vertical_speed.toFixed(2) + ' m/s'}}</span>
                </a-tooltip>
              </a-col>
              <a-col span="6">
                <a-tooltip title="Wind Speed">
                  <span>W.S</span>
                  <span class="ml10">{{ !deviceInfo.device || deviceInfo.device.wind_speed === str ? str : formatWindSpeed(deviceInfo.device.wind_speed / 10, 2) }}</span>
                </a-tooltip>
              </a-col>
            </a-row>
        </div>
      </div>
      <div class="battery-slide" v-if="deviceInfo.device && deviceInfo.device.battery.remain_flight_time !== 0" style="border: 1px solid red">
        <div style="background: #535759;" class="width-100"></div>
        <div class="capacity-percent" :style="{ width: deviceInfo.device.battery.capacity_percent + '%'}"></div>
        <div class="return-home" :style="{ width: deviceInfo.device.battery.return_home_power + '%'}"></div>
        <div class="landing" :style="{ width: deviceInfo.device.battery.landing_power + '%'}"></div>
        <div class="white-point" :style="{ left: deviceInfo.device.battery.landing_power + '%'}"></div>
        <div class="battery" :style="{ left: deviceInfo.device.battery.capacity_percent + '%' }">
          {{ Math.floor(deviceInfo.device.battery.remain_flight_time / 60) }}:
          {{ 10 > (deviceInfo.device.battery.remain_flight_time % 60) ? '0' : ''}}{{deviceInfo.device.battery.remain_flight_time % 60 }}
        </div>
      </div>
      <!-- 飞行指令 -->
      <DroneControlPanel v-if="!isMini" :sn="osdVisible.gateway_sn" :deviceInfo="deviceInfo" :payloads="osdVisible.payloads"></DroneControlPanel>
    </div>

    <!-- Circle radius modal -->
    <a-modal
      v-model:visible="circleRadiusVisible"
      title="Confirmer le rayon du cercle"
      ok-text="Valider"
      cancel-text="Annuler"
      @ok="onCircleRadiusConfirm"
      @cancel="onCircleRadiusCancel"
    >
      <p style="color:#aaa; margin-bottom: 8px;">Rayon calculé depuis le 2e point. Vous pouvez le modifier :</p>
      <a-input-number
        v-model:value="circleRadius"
        :min="1"
        :max="100000"
        :precision="0"
        style="width: 100%"
        addon-after="m"
        placeholder="Rayon en mètres"
      />
    </a-modal>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue'
import {
  generateLineContent,
  generatePointContent,
  generatePolyContent,
  generateCircleContent
} from '../utils/map-layer-utils'
import mapboxgl from 'mapbox-gl'
import { generatePoint } from '/@/utils/genjson'
import { postElementsReq, postGroupReq, getElementGroupsReq, mergeGroupReq } from '/@/api/layer'
import { getDeviceTopo } from '/@/api/manage'
import { ELocalStorageKey } from '/@/types/enums'
import EventBus from '/@/event-bus'
import { message, notification } from 'ant-design-vue'
import { DRC_METHOD } from '/@/types/drc'
import { MapDoodleType, MapElementEnum, MapDoodleColor } from '/@/constants/map'
import { useGMapManage, setTerrainExaggeration, getTerrainExaggeration, setTerrainSource, getTerrainSource, setBuildingsVisible, getBuildingsVisible } from '/@/hooks/use-g-map'
import { formatWindSpeed, toggleWindSpeedUnit, windUnitLabel } from '/@/utils/wind-speed'
import { useGMapCover } from '/@/hooks/use-g-map-cover'
import { useMouseTool } from '/@/hooks/use-mouse-tool'
import { getApp, getRoot } from '/@/root'
import { useMyStore } from '/@/store'
import { GeojsonCoordinate } from '/@/types/map'
import { MapDoodleEnum } from '/@/types/map-enum'
import { PostElementsBody } from '/@/types/mapLayer'
import { uuidv4 } from '/@/utils/uuid'
import { deviceTsaUpdate } from '/@/hooks/use-g-map-tsa'
import {
  DeviceOsd, DeviceStatus, DockOsd, EGear, EModeCode, GatewayOsd, EDockModeCode,
  NetworkStateQualityEnum, NetworkStateTypeEnum, RainfallEnum, DroneInDockEnum
} from '/@/types/device'
import pin from '/@/assets/icons/pin-2d8cf0.svg'
import M30 from '/@/assets/icons/m30.png'
import Mavic3E from '/@/assets/icons/mavic-3e.png'
import {
  BorderOutlined, LineOutlined, CloseOutlined, ControlOutlined, TrademarkOutlined, ArrowDownOutlined,
  ThunderboltOutlined, SignalFilled, GlobalOutlined, HistoryOutlined, CloudUploadOutlined, RocketOutlined,
  FieldTimeOutlined, CloudOutlined, CloudFilled, FolderOpenOutlined, RobotFilled, ArrowUpOutlined, CarryOutOutlined
} from '@ant-design/icons-vue'
import { EDeviceTypeName } from '../types'
import DockControlPanel from './g-map/DockControlPanel.vue'
import { useDockControl } from './g-map/use-dock-control'
import DroneControlPanel from './g-map/DroneControlPanel.vue'
import { useConnectMqtt } from './g-map/use-connect-mqtt'
import FlightAreaActionIcon from './flight-area/FlightAreaActionIcon.vue'
import MapOverlaysPanel from './MapOverlaysPanel.vue'
import { useMapOverlays } from '/@/hooks/use-map-overlays'
import { EFlightAreaType } from '../types/flight-area'
import { useFlightArea } from './flight-area/use-flight-area'
import { useFlightAreaDroneLocationEvent } from './flight-area/use-flight-area-drone-location-event'

export default defineComponent({
  components: {
    BorderOutlined,
    LineOutlined,
    CloseOutlined,
    ControlOutlined,
    TrademarkOutlined,
    ThunderboltOutlined,
    SignalFilled,
    GlobalOutlined,
    HistoryOutlined,
    CloudUploadOutlined,
    FieldTimeOutlined,
    CloudOutlined,
    CloudFilled,
    FolderOpenOutlined,
    RobotFilled,
    ArrowUpOutlined,
    ArrowDownOutlined,
    DockControlPanel,
    DroneControlPanel,
    CarryOutOutlined,
    RocketOutlined,
    FlightAreaActionIcon,
    MapOverlaysPanel,
  },
  name: 'GMap',
  props: {
    isMini: { type: Boolean, default: false },
  },
  setup (props) {
    const useMouseToolHook = useMouseTool()
    const useGMapManageHook = useGMapManage()
    const deviceTsaUpdateHook = deviceTsaUpdate()
    const useGMapCoverHook = useGMapCover()
    const { initOverlays } = useMapOverlays()
    const root = getRoot()

    // Keep the Mapbox canvas in sync with its container size. The layout fires
    // many resize triggers (panel collapse, drawer, split, window), and any miss
    // leaves the canvas smaller than `.right`, exposing its black background.
    // A ResizeObserver covers every case without scattering manual resize() calls.
    let mapResizeObserver: ResizeObserver | null = null
    let mapResizeRaf = 0

    const canAddAnnotations = parseInt(localStorage.getItem('user_type') || '1') !== 5

    const mouseMode = ref(false)
    const circleRadiusVisible = ref(false)
    const circleRadius = ref(100)
    const pendingCircleObj = ref<any>(null)
    const store = useMyStore()
    const state = reactive({
      currentType: '',
      coverIndex: 0,
      isFlightArea: false,
    })
    const str: string = '--'

    // ── Drone trail (visuel live uniquement — l'enregistrement est matérialisé côté backend) ──
    const TRAIL_SOURCE = 'drone-trail-source'
    const TRAIL_LAYER = 'drone-trail-layer'
    const MAX_TRAIL_POINTS = 2000
    // Chaque point : [lng, lat, mslAltitude] — mslAltitude (m ASL, absolu) place la trace à
    // l'altitude réelle du drone via line-z-offset en référence 'sea' (pas de dépendance terrain).
    const droneTrailCoords: Record<string, [number, number, number][]> = {}
    // Completed trails (drone disconnected) — kept on map until next connection
    const staleTrailCoords: Record<string, [number, number, number][]> = {}
    // Last known mode_code per drone SN — used to detect airborne→standby transition
    const prevModeCode: Record<string, number> = {}
    // Altitude MSL du terrain au décollage (capturée quand elevation ≈ 0), par drone SN.
    // ASL du drone = ce terrain + elevation (hauteur relative au décollage) → pas de géoïde.
    const takeoffTerrainMSL: Record<string, number> = {}
    let trailReady = false

    // One LineString feature per trail. The per-vertex MSL (ASL) altitudes are carried in
    // an `elevation` array property; the layer's line-z-offset (sea-reference) reads them
    // back so the line sits at the drone's real absolute altitude in 3D.
    function getTrailGeoJson () {
      const all = [
        ...Object.values(droneTrailCoords),
        ...Object.values(staleTrailCoords),
      ]
      const features = all
        .filter(coords => coords.length > 1)
        .map(coords => ({
          type: 'Feature' as const,
          properties: { elevation: coords.map(c => c[2]) },
          geometry: {
            type: 'LineString' as const,
            coordinates: coords.map(c => [c[0], c[1]]),
          },
        }))
      return { type: 'FeatureCollection' as const, features }
    }

    function refreshTrailSource () {
      const map = root.$map
      if (!map) return
      const src = map.getSource(TRAIL_SOURCE) as any
      if (src?.setData) src.setData(getTrailGeoJson())
    }

    function clearTrailLayer () {
      trailReady = false
      const map = root.$map
      if (!map) return
      if (map.getLayer(TRAIL_LAYER)) map.removeLayer(TRAIL_LAYER)
      if (map.getSource(TRAIL_SOURCE)) map.removeSource(TRAIL_SOURCE)
    }

    function initTrailLayer (map: any) {
      if (map.getSource(TRAIL_SOURCE)) return
      // lineMetrics: true is required so line-progress (and thus the per-vertex
      // elevation lookup below) is available.
      map.addSource(TRAIL_SOURCE, { type: 'geojson', lineMetrics: true, data: getTrailGeoJson() })
      map.addLayer({
        id: TRAIL_LAYER,
        type: 'line',
        source: TRAIL_SOURCE,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          // Trace en altitude ABSOLUE (ASL) : référence 'sea' + z-offset = MSL par sommet.
          // line-progress (0→1) est mappé sur un index fractionnaire dans le tableau
          // `elevation` (les altitudes MSL) et interpolé. Aucune dépendance au terrain.
          'line-elevation-reference': 'sea',
          'line-z-offset': [
            'at-interpolated',
            ['*', ['line-progress'], ['-', ['length', ['get', 'elevation']], 1]],
            ['get', 'elevation'],
          ],
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': 3,
          'line-opacity': 0.9,
          // En 3D incliné, la portion de la ligne élevée passant DERRIÈRE le relief est,
          // par défaut, masquée par le terrain (opacity occultée = 0 → "une partie disparaît").
          // On la garde visible en semi-transparence pour voir la trace complète.
          'line-occlusion-opacity': 0.5,
        } as any,
      })
      trailReady = true
    }

    function pushTrailPoint (lng: number, lat: number, mslAlt: number, droneSn?: string) {
      if (!lng || !lat || !droneSn) return
      if (!droneTrailCoords[droneSn]) droneTrailCoords[droneSn] = []
      droneTrailCoords[droneSn].push([lng, lat, mslAlt])
      if (droneTrailCoords[droneSn].length > MAX_TRAIL_POINTS) droneTrailCoords[droneSn].shift()
      const map = root.$map
      if (!map) return
      if (!trailReady) initTrailLayer(map)
      refreshTrailSource()
    }
    // NB : l'ENREGISTREMENT du tracé de vol (annotation « Vol … » sous « Historique déplacement »
    // > [drone]) est désormais MATÉRIALISÉ CÔTÉ BACKEND depuis la télémétrie osdRcDrone
    // (DroneTrailServiceImpl), en altitude absolue ASL. Le front ne dessine plus que la trace
    // LIVE ci-dessus (visuel temps réel) ; il ne crée/sauvegarde plus rien. Les traces
    // sauvegardées apparaissent via renderLayersOnMap comme n'importe quelle annotation.
    // ─────────────────────────────────────────────────────────────────────────

    // Geoid correction (WGS84 ellipsoidal → orthometric/MSL).
    // Auto-calibrated once when the drone is on the ground (elevation ≈ 0):
    //   geoidOffset = raw_WGS84_height − Mapbox_terrain_elevation(lat, lng)
    // This offset (~43–50 m in France) is then subtracted from every height reading.
    let geoidOffset: number | null = null

    function applyGeoidCorrection (rawHeight: number): number {
      if (geoidOffset == null) return rawHeight
      return rawHeight - geoidOffset
    }

    function tryCalibrate (height: number, elevation: number, lat: number, lng: number) {
      if (geoidOffset != null) return
      const numH = Number(height)
      const numE = Number(elevation)
      if (isNaN(numH) || isNaN(numE) || !lat || !lng) return
      const map = root.$map
      if (!map) return
      const terrain = (map as any).queryTerrainElevation({ lng, lat }, { exaggerated: false })
      if (terrain == null) return
      // N (geoid undulation) = WGS84 − MSL
      // WGS84 du sol ≈ height − elevation (soustrait la hauteur relative au point de décollage)
      // MSL du sol ≈ élévation Mapbox DEM à la position courante du drone
      // Hypothèse : terrain sous le drone ≈ terrain au point de décollage (bon pour terrains plats/vallonnés)
      geoidOffset = numH - numE - terrain
      store.commit('SET_GEOID_OFFSET', geoidOffset)
    }

    /**
     * Convert Mapbox MSL elevation → WGS84 ellipsoidal altitude expected by DJI.
     * WGS84 = MSL + geoidOffset  (≈ +47 m in France)
     * If not yet calibrated, returns MSL as-is (minor error until first drone lands).
     */
    function toAnnotationAltitude (elevationMSL: number): number {
      if (geoidOffset == null) return elevationMSL
      return elevationMSL + geoidOffset
    }

    const deviceInfo = reactive({
      gateway: {
        capacity_percent: str,
        transmission_signal_quality: str,
      } as GatewayOsd,
      dock: {

      } as DockOsd,
      device: {
        gear: -1,
        mode_code: EModeCode.Disconnected,
        height: str,
        home_distance: str,
        horizontal_speed: str,
        vertical_speed: str,
        wind_speed: str,
        wind_direction: str,
        elevation: str,
        position_state: {
          gps_number: str,
          is_fixed: 0,
          rtk_number: str
        },
        battery: {
          capacity_percent: str,
          landing_power: str,
          remain_flight_time: 0,
          return_home_power: str,
        },
        latitude: 0,
        longitude: 0,
      } as DeviceOsd
    })
    const shareId = computed(() => {
      return store.state.layerBaseInfo.share
    })
    const defaultId = computed(() => {
      return store.state.layerBaseInfo.default
    })
    // Groupe cible pour les nouvelles annotations : activeGroupId ou fallback selon le rôle
    const annotationTargetGroupId = computed(() =>
      store.state.activeGroupId || (isPCUser ? defaultId.value : shareId.value)
    )
    const drawVisible = computed(() => {
      return store.state.drawVisible
    })
    const livestreamOthersVisible = computed(() => {
      return store.state.livestreamOthersVisible
    })
    const livestreamAgoraVisible = computed(() => {
      return store.state.livestreamAgoraVisible
    })
    const osdVisible = computed(() => {
      return store.state.osdVisible
    })
    // OSD centré en bas, décalé à gauche si la modal live est visible (pour éviter superposition)
    // OSD centré en bas, décalé à gauche si la modal live est visible
    const osdPositionStyle = computed(() => {
      const liveVisible = store.state.livestreamOthersVisible || store.state.livestreamAgoraVisible
      if (liveVisible) {
        // Centré sur la zone libre (tout sauf les 25vw de la modal live à droite)
        return { left: 'calc((100% - 25vw) / 2 - 240px)', right: 'auto', bottom: '10px', top: 'auto' }
      }
      return { left: 'calc(50% - 240px)', right: 'auto', bottom: '10px', top: 'auto' }
    })
    const qualityStyle = computed(() => {
      if (deviceInfo.dock.basic_osd?.network_state?.type === NetworkStateTypeEnum.ETHERNET ||
        (deviceInfo.dock.basic_osd?.network_state?.quality || 0) > NetworkStateQualityEnum.FAIR) {
        return 'color: #00ee8b'
      }
      if ((deviceInfo.dock.basic_osd?.network_state?.quality || 0) === NetworkStateQualityEnum.FAIR) {
        return 'color: yellow'
      }
      return 'color: red'
    })
    watch(() => store.state.deviceStatusEvent,
      data => {
        if (Object.keys(data.deviceOnline).length !== 0) {
          deviceTsaUpdateHook.initMarker(data.deviceOnline.domain, data.deviceOnline.device_callsign, data.deviceOnline.sn)
          if (data.deviceOnline.domain === EDeviceTypeName.Aircraft) {
            if (data.deviceOnline.sn && data.deviceOnline.device_callsign) {
              store.commit('SET_SN_TO_CALLSIGN', { sn: data.deviceOnline.sn, callsign: data.deviceOnline.device_callsign })
            }
            notification.warning({
              message: 'Drone détecté',
              description: `${data.deviceOnline.device_callsign || data.deviceOnline.sn} vient de se connecter.`,
              placement: 'topRight',
              duration: 8,
            })
          }
          store.state.deviceStatusEvent.deviceOnline = {} as DeviceStatus
        }
        if (Object.keys(data.deviceOffline).length !== 0) {
          if (data.deviceOffline.sn) {
            const offlineSn = data.deviceOffline.sn
            if (droneTrailCoords[offlineSn]) {
              // Plus de sauvegarde côté front : le backend matérialise le tracé (balayage des
              // traces stale). On garde juste la trace LIVE visible en « stale » jusqu'à la
              // prochaine connexion, pour ne pas perdre le visuel en cas de coupure de signal.
              staleTrailCoords[offlineSn] = droneTrailCoords[offlineSn]
              delete droneTrailCoords[offlineSn]
              refreshTrailSource()
            }
          }
          deviceTsaUpdateHook.removeMarker(data.deviceOffline.sn)
          deviceTsaUpdateHook.removeHomeMarker()
          store.commit('SET_RTH_HOME_POINT', null)
          if ((data.deviceOffline.sn === osdVisible.value.sn) || (osdVisible.value.is_dock && data.deviceOffline.sn === osdVisible.value.gateway_sn)) {
            osdVisible.value.visible = false
            store.commit('SET_OSD_VISIBLE_INFO', osdVisible)
          }
          store.state.deviceStatusEvent.deviceOffline = {}
        }
      },
      {
        deep: true
      }
    )

    // Initialise deviceInfo immédiatement à l'ouverture de l'OSD
    watch(() => store.state.osdVisible, (val) => {
      if (val.visible) {
        const state = store.state.deviceState
        if (val.gateway_sn && state.gatewayInfo[val.gateway_sn]) {
          deviceInfo.gateway = state.gatewayInfo[val.gateway_sn]
        }
        if (val.sn && state.deviceInfo[val.sn]) {
          deviceInfo.device = state.deviceInfo[val.sn]
        }
      }
    }, { deep: true })

    watch(() => store.state.deviceState, data => {
      if (data.currentType === EDeviceTypeName.Gateway && data.gatewayInfo[data.currentSn]) {
        const { longitude, latitude } = data.gatewayInfo[data.currentSn]
        deviceTsaUpdateHook.moveTo(data.currentSn, longitude, latitude)
        if (osdVisible.value.visible && osdVisible.value.gateway_sn !== '') {
          deviceInfo.gateway = data.gatewayInfo[osdVisible.value.gateway_sn]
        }
      }
      if (data.currentType === EDeviceTypeName.Aircraft && data.deviceInfo[data.currentSn]) {
        const { longitude, latitude } = data.deviceInfo[data.currentSn]
        const d = data.deviceInfo[data.currentSn]

        // Altitude ABSOLUE (ASL/MSL) du drone — marqueur ET trace en absolu, PAS plaqué au sol.
        // Méthode directe, sans géoïde : ASL = altitude MSL du terrain au DÉCOLLAGE + `elevation`
        // (hauteur relative au décollage, la mesure verticale la plus fiable de DJI). On capture
        // le terrain de décollage quand le drone est au sol (elevation ≈ 0).
        // Replis quand l'ancrage manque (page ouverte drone déjà en vol) : géoïde si calibré
        // (height WGS84 − offset), sinon terrain courant + elevation.
        const rawWgs84 = Number(d?.height)
        const heightAGL = Number(d?.elevation ?? 0) || 0
        const terrainZ = (root.$map as any)?.queryTerrainElevation({ lng: longitude, lat: latitude }, { exaggerated: false }) ?? 0
        if (heightAGL <= 2 && isFinite(terrainZ)) {
          takeoffTerrainMSL[data.currentSn] = terrainZ
        }
        const takeoffMSL = takeoffTerrainMSL[data.currentSn]
        const absoluteAlt = takeoffMSL != null
          ? takeoffMSL + heightAGL
          : ((geoidOffset != null && isFinite(rawWgs84)) ? applyGeoidCorrection(rawWgs84) : (terrainZ + heightAGL))

        // Position marker at actual absolute (ASL) flight altitude
        deviceTsaUpdateHook.moveTo(data.currentSn, longitude, latitude, absoluteAlt)

        // Rotate icon: prefer attitude_head (drone IMU/compass, 0-360°).
        // Fallback: bearing from last two trail points, but only if the drone moved
        // at least 5 m (avoids random orientations from GPS jitter when hovering).
        if (d?.attitude_head != null) {
          deviceTsaUpdateHook.rotateTo(data.currentSn, d.attitude_head)
        } else {
          const trail = droneTrailCoords[data.currentSn]
          if (trail && trail.length >= 2) {
            const [lng1, lat1] = trail[trail.length - 2]
            const [lng2, lat2] = trail[trail.length - 1]
            const R = 6371000; const r = Math.PI / 180
            const dLat = (lat2 - lat1) * r; const dLng = (lng2 - lng1) * r
            const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLng / 2) ** 2
            const dist = 2 * R * Math.asin(Math.sqrt(a))
            if (dist >= 5) {
              const y = Math.sin(dLng) * Math.cos(lat2 * r)
              const x = Math.cos(lat1 * r) * Math.sin(lat2 * r) - Math.sin(lat1 * r) * Math.cos(lat2 * r) * Math.cos(dLng)
              deviceTsaUpdateHook.rotateTo(data.currentSn, (Math.atan2(y, x) * 180 / Math.PI + 360) % 360)
            }
          }
        }

        // Trace live (visuel uniquement) — seulement en vol. On stocke l'altitude ABSOLUE
        // (ASL/MSL), rendue en référence 'sea'. L'ENREGISTREMENT est fait par le backend.
        if (d && d.mode_code >= EModeCode.Manual && d.mode_code <= EModeCode.Three_Blades_Landing) {
          pushTrailPoint(longitude, latitude, absoluteAlt, data.currentSn)
        }

        // Fin de vol : à l'atterrissage (vol → Standby), on purge la trace LIVE (une trace
        // par vol). Le backend a matérialisé l'annotation « Vol … » et émet un refresh qui la
        // fera apparaître. Plus aucune sauvegarde côté front.
        if (d) {
          const prevMode = prevModeCode[data.currentSn]
          const curMode = d.mode_code
          prevModeCode[data.currentSn] = curMode
          const wasAirborne = prevMode != null &&
            prevMode >= EModeCode.Manual &&
            prevMode <= EModeCode.Three_Blades_Landing
          const isNowStandby = curMode === EModeCode.Standby
          if (wasAirborne && isNowStandby) {
            delete droneTrailCoords[data.currentSn]
            refreshTrailSource()
          }
        }

        // OSD panel data + geoid calibration (altitude display only, unrelated to icon orientation)
        if (osdVisible.value.visible && osdVisible.value.sn !== '') {
          deviceInfo.device = data.deviceInfo[osdVisible.value.sn]
        }
        if (d) tryCalibrate(d.height, d.elevation, latitude, longitude)
      }
      if (data.currentType === EDeviceTypeName.Dock && data.dockInfo[data.currentSn]) {
        const { longitude, latitude } = data.dockInfo[data.currentSn].basic_osd ?? {}
        deviceTsaUpdateHook.initMarker(EDeviceTypeName.Dock, EDeviceTypeName[EDeviceTypeName.Dock], data.currentSn, longitude, latitude)
        if (osdVisible.value.visible && osdVisible.value.is_dock && osdVisible.value.gateway_sn !== '') {
          deviceInfo.dock = data.dockInfo[osdVisible.value.gateway_sn]
          deviceInfo.device = data.deviceInfo[deviceInfo.dock.basic_osd.sub_device?.device_sn ?? osdVisible.value.sn]
        }
      }
    }, {
      deep: true
    })

    watch(
      () => store.state.wsEvent,
      newData => {
        const event = newData
        let exist = false
        if (Object.keys(event.mapElementCreat).length !== 0) {
          console.log(event.mapElementCreat)
          const ele = event.mapElementCreat
          // Positions d'équipe : gérées comme marqueurs-avatars → ne pas dessiner de pin
          if (isUserPositionElement(ele)) {
            store.state.wsEvent.mapElementCreat = {}
            return
          }
          store.state.Layers.forEach(layer => {
            layer.elements.forEach(e => {
              if (e.id === ele.id) {
                exist = true
                console.log('true')
              }
            })
          })
          if (exist === false) {
            setLayers({
              id: ele.id,
              name: ele.name,
              resource: ele.resource
            })

            const data = { id: ele.id, name: ele.name }
            const height = deriveElementHeight(ele)
            const curtain = curtainEnabled(ele.id)
            if (MapElementEnum.PIN === ele.resource?.type) {
              // Honore le mode d'élévation (ground/agl/asl) dès la création temps réel, comme au reload.
              const ev = resolveElev(ele)
              useGMapCoverHook.init2DPin(
                ele.name,
                ele.resource.content.geometry.coordinates,
                ele.resource.content.properties.color,
                data,
                ev.height,
                { mode: ev.mode, fixedAltitude: ev.fixedAltitude, curtain }
              )
            } else if (MapElementEnum.LINE === ele.resource?.type) {
              const p = ele.resource.content.properties
              const isTrail = p?.altitudeMode === 'asl' || (p?.clampToGround === false && p?.directConnected === false)
              const ev = resolveElev(ele)
              const perVertex = isTrail && ev.mode === 'msl'
              useGMapCoverHook.initPolyline(
                ele.name,
                ele.resource.content.geometry.coordinates,
                p.color,
                data,
                ev.height,
                perVertex,
                { mode: ev.mode, fixedAltitude: ev.fixedAltitude, curtain })
            } else if (MapElementEnum.POLY === ele.resource?.type || MapElementEnum.CIRCLE === ele.resource?.type) {
              const geo = ele.resource.content.geometry
              if (geo?.type === 'Circle') {
                useGMapCoverHook.initCircle(ele.name, geo.coordinates, geo.radius, data, ele.resource.content.properties.color)
              } else {
                useGMapCoverHook.initPolygon(
                  ele.name,
                  ele.resource.content.geometry.coordinates,
                  ele.resource.content.properties.color,
                  data,
                  height)
              }
            }
          }

          store.state.wsEvent.mapElementCreat = {}
        }
        if (Object.keys(event.mapElementUpdate).length !== 0) {
          console.log(event.mapElementUpdate)
          console.log('该功能还未实现，请开发商自己增加')
          store.state.wsEvent.mapElementUpdate = {}
        }
        if (Object.keys(event.mapElementDelete).length !== 0) {
          console.log(event.mapElementDelete)
          console.log('该功能还未实现，请开发商自己增加')
          store.state.wsEvent.mapElementDelete = {}
        }
      },
      {
        deep: true
      }
    )

    // RTH home point: display the actual home point received from the RC via WebSocket
    watch(
      () => store.state.rthHomePoint,
      (hp) => {
        if (hp && hp.lng && hp.lat) {
          deviceTsaUpdateHook.initHomeMarker(hp.lng, hp.lat)
        } else {
          deviceTsaUpdateHook.removeHomeMarker()
        }
      },
      { deep: true }
    )

    function draw (type: MapDoodleType, bool: boolean, flightAreaType?: EFlightAreaType) {
      state.currentType = type
      mouseMode.value = bool
      state.isFlightArea = !!flightAreaType
      useMouseToolHook.mouseTool(type, getDrawCallback, flightAreaType)
    }

    // dock 控制面板
    const {
      dockControlPanelVisible,
      setDockControlPanelVisible,
      onCloseControlPanel,
    } = useDockControl()

    // 连接或断开drc
    useConnectMqtt()

    // Hauteur AGL redérivée du Z stocké (les propriétés custom ne survivent pas au
    // backend → properties.height est perdu après rechargement). Médiane de (Z − terrain).
    function deriveElementHeight (ele: any): number {
      const map = root.$map as any
      const geo = ele?.resource?.content?.geometry
      const fallback = Number(ele?.resource?.content?.properties?.height) || 0
      if (!map || !geo) return fallback
      let coords: number[][] = []
      if (geo.type === 'Point' || geo.type === 'Circle') coords = [geo.coordinates]
      else if (geo.type === 'LineString') coords = geo.coordinates
      else if (geo.type === 'Polygon') coords = geo.coordinates[0]
      const diffs: number[] = []
      for (const c of coords) {
        const z = Number(c[2])
        if (!isFinite(z)) continue
        const t = map.queryTerrainElevation({ lng: c[0], lat: c[1] }, { exaggerated: false })
        if (t == null || isNaN(t)) continue
        diffs.push(z - t)
      }
      if (!diffs.length) return fallback
      diffs.sort((a, b) => a - b)
      const h = Math.round(diffs[Math.floor(diffs.length / 2)])
      return h > 0 ? h : 0
    }

    // Mode d'élévation redérivé du Z stocké (aucun flag dédié ne survit au backend).
    //  - 'agl' : Z = terrain + h → (Z−terrain) ≈ constant le long de l'élément.
    //  - 'msl' : Z = altitude fixe → Z ≈ constant, mais (Z−terrain) varie avec le relief.
    // On compare l'amplitude des deux séries : la plus stable désigne le mode. Rétro-
    // compatible (les lignes AGL existantes ont (Z−terrain) constant → restent 'agl').
    function deriveElevation (ele: any): { mode: 'agl' | 'msl', height: number, fixedAltitude: number } {
      const map = root.$map as any
      const geo = ele?.resource?.content?.geometry
      const fallbackH = Number(ele?.resource?.content?.properties?.height) || 0
      const out = { mode: 'agl' as 'agl' | 'msl', height: fallbackH, fixedAltitude: 0 }
      if (!map || !geo) return out
      let coords: number[][] = []
      if (geo.type === 'Point' || geo.type === 'Circle') coords = [geo.coordinates]
      else if (geo.type === 'LineString') coords = geo.coordinates
      else if (geo.type === 'Polygon') coords = geo.coordinates[0]
      const zs: number[] = []
      const agls: number[] = []
      for (const c of coords) {
        const z = Number(c[2])
        if (!isFinite(z)) continue
        const t = map.queryTerrainElevation({ lng: c[0], lat: c[1] }, { exaggerated: false })
        if (t == null || isNaN(t)) continue
        zs.push(z)
        agls.push(z - t)
      }
      if (!zs.length) return out
      const median = (arr: number[]) => { const s = [...arr].sort((a, b) => a - b); return s[Math.floor(s.length / 2)] }
      const range = (arr: number[]) => Math.max(...arr) - Math.min(...arr)
      const medAgl = median(agls)
      const medZ = median(zs)
      // Élément posé au sol (pas élevé).
      if (medAgl <= 1 && range(agls) <= 2) return out
      // Plus d'1 sommet : on peut distinguer fixe vs suivi via l'amplitude.
      if (zs.length > 1 && range(zs) + 0.5 < range(agls)) {
        out.mode = 'msl'
        out.fixedAltitude = Math.round(medZ)
        out.height = 0
      } else {
        out.mode = 'agl'
        out.height = Math.max(0, Math.round(medAgl))
      }
      return out
    }

    // Élévation pour le rendu, en privilégiant le flag PERSISTÉ altitudeMode (source de
    // vérité) ; repli sur l'heuristique deriveElevation pour les annotations legacy.
    //  ground → 'agl' hauteur 0 (plaqué) ; agl → 'agl' + hauteur ; asl → 'msl' + altitude MSL.
    function resolveElev (ele: any): { mode: 'agl' | 'msl', height: number, fixedAltitude: number } {
      const am = ele?.resource?.content?.properties?.altitudeMode
      if (am === 'ground') return { mode: 'agl', height: 0, fixedAltitude: 0 }
      if (am === 'agl') return { mode: 'agl', height: deriveElementHeight(ele), fixedAltitude: 0 }
      if (am === 'asl') {
        const geo = ele?.resource?.content?.geometry
        let coords: number[][] = []
        if (geo?.type === 'Point' || geo?.type === 'Circle') coords = [geo.coordinates]
        else if (geo?.type === 'LineString') coords = geo.coordinates
        else if (geo?.type === 'Polygon') coords = geo.coordinates[0]
        const zs = coords.map(c => Number(c[2])).filter(z => isFinite(z)).sort((a, b) => a - b)
        return { mode: 'msl', height: 0, fixedAltitude: zs.length ? Math.round(zs[Math.floor(zs.length / 2)]) : 0 }
      }
      return deriveElevation(ele)
    }

    function curtainEnabled (id: string): boolean {
      try { return localStorage.getItem('curtain:' + id) === '1' } catch (_) { return false }
    }

    // Groupe système géré par le backend : les positions d'équipe s'affichent déjà
    // comme marqueurs-avatars (useGMapUserLocations). On NE les rend donc PAS ici
    // comme pins génériques pour éviter un double affichage sur le web.
    const USER_POSITIONS_GROUP = 'Positions équipe'
    function userPositionsGroupId (): string | undefined {
      const g = (store.state.Layers as any[]).find((l: any) => l.name === USER_POSITIONS_GROUP)
      return g?.id
    }

    // Un élément « position d'équipe » ne doit JAMAIS être dessiné en pin (il est déjà
    // affiché en marqueur-avatar). On le reconnaît par son groupe ; mais la 1re fois
    // qu'un utilisateur partage, le backend crée le groupe APRÈS le chargement de la
    // page → le group_id n'est pas encore dans nos Layers. Repli : un POINT dont le nom
    // correspond à un utilisateur en cours de partage est une position d'équipe.
    function isUserPositionElement (ele: any): boolean {
      const gid = (ele as any)?.group_id
      const upGid = userPositionsGroupId()
      if (upGid && gid === upGid) return true
      if (MapElementEnum.PIN === ele?.resource?.type) {
        const sharing = Object.values(store.state.userLocations || {})
          .some((u: any) => u?.username && u.username === ele?.name)
        if (sharing) return true
      }
      return false
    }

    function renderLayersOnMap (layers: any[]) {
      layers.forEach(layer => {
        if (!layer.elements) return
        if (layer.name === USER_POSITIONS_GROUP) return
        layer.elements.forEach((ele: any) => {
          if (!ele.resource) return
          const data = { id: ele.id, name: ele.name }
          const curtain = curtainEnabled(ele.id)
          if (MapElementEnum.PIN === ele.resource.type) {
            // Élévation pilotée par le flag persisté altitudeMode : 'ground' (dont pins Pilot/RC)
            // → plaqué ; 'agl' → hauteur au-dessus du sol ; 'asl' → altitude absolue. Repli
            // heuristique pour les pins legacy (élevés seulement si clampToGround === false).
            const ev = ele.resource.content.properties?.altitudeMode
              ? resolveElev(ele)
              : (ele.resource.content.properties?.clampToGround === false
                  ? deriveElevation(ele)
                  : { mode: 'agl' as const, height: 0, fixedAltitude: 0 })
            useGMapCoverHook.init2DPin(ele.name, ele.resource.content.geometry.coordinates, ele.resource.content.properties.color, data, ev.height, { mode: ev.mode, fixedAltitude: ev.fixedAltitude, curtain })
          } else if (MapElementEnum.LINE === ele.resource.type) {
            const p = ele.resource.content.properties
            // Trace de vol = altitude absolue (asl) sur toute la ligne → per-vertex sea-reference.
            const isTrail = p?.altitudeMode === 'asl' || (p?.clampToGround === false && p?.directConnected === false)
            const ev = resolveElev(ele)
            const perVertex = isTrail && ev.mode === 'msl'
            useGMapCoverHook.initPolyline(ele.name, ele.resource.content.geometry.coordinates, p.color, data, ev.height, perVertex, { mode: ev.mode, fixedAltitude: ev.fixedAltitude, curtain })
          } else if (MapElementEnum.POLY === ele.resource.type || MapElementEnum.CIRCLE === ele.resource.type) {
            const geo = ele.resource.content.geometry
            if (geo.type === 'Circle') {
              useGMapCoverHook.initCircle(ele.name, geo.coordinates, geo.radius, data, ele.resource.content.properties.color)
            } else {
              const p = ele.resource.content.properties
              const ev = deriveElevation(ele)
              // Volume (plancher/plafond) si présent en propriétés (persisté backend) ; sinon élévation simple.
              useGMapCoverHook.initPolygon(ele.name, geo.coordinates, p.color, data, ev.height, {
                mode: ev.mode,
                fixedAltitude: ev.fixedAltitude,
                curtain,
                floor: p.floor,
                floorRef: p.floorReference,
                ceiling: p.ceiling,
                ceilingRef: p.ceilingReference,
              })
            }
          }
        })
      })
    }

    // ── Cadrage auto de la mini-carte sur toutes les annotations ─────────────
    // En mode mini (PiP, quand le live est en grand), on garde en permanence
    // l'ensemble des annotations visibles : on refait le cadrage quand la carte
    // passe en mini, quand les annotations changent, et quand le conteneur est
    // redimensionné. On NE refait PAS le cadrage sur le déplacement du drone
    // pour ne pas "sauter" en continu.
    function collectAnnotationCoords (): [number, number][] {
      const out: [number, number][] = []
      const push = (c: any) => {
        if (Array.isArray(c) && typeof c[0] === 'number' && typeof c[1] === 'number') out.push([c[0], c[1]])
      }
      store.state.Layers.forEach((layer: any) => {
        (layer.elements || []).forEach((ele: any) => {
          const geo = ele?.resource?.content?.geometry
          if (!geo) return
          const co = geo.coordinates
          switch (geo.type) {
            case 'Point':
              push(co)
              break
            case 'Circle': {
              // Centre ± rayon pour que tout le disque tienne dans le cadre.
              const [lng, lat] = co
              const r = Number(geo.radius) || 0
              const dLat = r / 111320
              const dLng = r / (111320 * Math.cos((lat * Math.PI) / 180) || 1)
              push([lng - dLng, lat - dLat]); push([lng + dLng, lat + dLat])
              break
            }
            case 'LineString':
              (co || []).forEach(push)
              break
            case 'Polygon':
              (co?.[0] || []).forEach(push)
              break
          }
        })
      })
      return out
    }

    let fitMiniTimer: ReturnType<typeof setTimeout> | null = null
    function fitMiniToAnnotations () {
      if (!props.isMini) return
      const map: any = root.$map
      if (!map) return
      const coords = collectAnnotationCoords()
      if (coords.length === 0) return
      let west = Infinity; let south = Infinity; let east = -Infinity; let north = -Infinity
      coords.forEach(([lng, lat]) => {
        if (lng < west) west = lng
        if (lng > east) east = lng
        if (lat < south) south = lat
        if (lat > north) north = lat
      })
      try {
        map.fitBounds([[west, south], [east, north]], { padding: 30, maxZoom: 16, duration: 0 })
      } catch (_) { /* carte pas prête */ }
    }
    function scheduleFitMini () {
      if (fitMiniTimer) clearTimeout(fitMiniTimer)
      fitMiniTimer = setTimeout(fitMiniToAnnotations, 150)
    }

    // Recadre dès que la carte passe en mini (le live vient de passer en grand).
    watch(() => props.isMini, (mini) => { if (mini) scheduleFitMini() })
    // Recadre quand les annotations changent (création, refresh de groupe…).
    watch(() => store.state.Layers, () => scheduleFitMini(), { deep: true })

    // ── PC Annotations group setup (GMap side) ───────────────────────────────
    const PC_ANNOTATIONS_GROUP = 'PC Annotations'
    const isPCUser = parseInt(localStorage.getItem('user_type') || '1') !== 2

    async function setupPCActiveGroup (layers: any[]) {
      if (!isPCUser) return
      const username = localStorage.getItem('username') || 'PC'
      // Find or create PC Annotations top-level group
      const pcGroup = layers.find((g: any) => g.name === PC_ANNOTATIONS_GROUP && !g.parent_id)
      let pcGroupId = pcGroup?.id
      if (!pcGroupId) {
        try {
          const res = await postGroupReq(PC_ANNOTATIONS_GROUP)
          pcGroupId = res?.data?.id
          if (pcGroupId) {
            const refreshed = await getElementGroupsReq({ groupId: '' })
            if (refreshed?.data) {
              store.dispatch('setLayerInfo', refreshed.data)
              store.commit('SET_LAYER_INFO', refreshed.data)
              layers = refreshed.data
            }
          }
        } catch (e) { console.warn('[GMap] create PC Annotations:', e) }
      }
      if (!pcGroupId) return
      // Find or create user sub-group
      const sub = layers.find((g: any) => g.parent_id === pcGroupId && g.name === username)
      let subId = sub?.id
      if (!subId) {
        try {
          const res = await postGroupReq(username, pcGroupId)
          subId = res?.data?.id
          if (subId) {
            const refreshed = await getElementGroupsReq({ groupId: '' })
            if (refreshed?.data) {
              store.dispatch('setLayerInfo', refreshed.data)
              store.commit('SET_LAYER_INFO', refreshed.data)
            }
          }
        } catch (e) { console.warn('[GMap] create PC user sub-group:', e) }
      }
      if (subId) store.commit('SET_ACTIVE_GROUP', subId)
    }
    // ─────────────────────────────────────────────────────────────────────────

    // ── Rotation de l'icône drone depuis le cap DRC MQTT ────────────────────
    function handleDrcMarkerRotation (payload: any) {
      if (payload?.method !== DRC_METHOD.OSD_INFO_PUSH) return
      const heading = payload.data?.attitude_head
      if (heading == null) return
      // Applique la rotation sur le marker du drone actif (OSD visible ou premier drone connu)
      const sn = store.state.osdVisible?.sn || Object.keys(store.state.deviceState.deviceInfo)[0]
      if (sn) deviceTsaUpdateHook.rotateTo(sn, heading)
    }

    onBeforeUnmount(() => {
      EventBus.off('droneControlMqttInfo', handleDrcMarkerRotation)
      EventBus.off('placeMediaOnMap', onPlaceMediaOnMap)
      removeMediaPinDraft()
      mapResizeObserver?.disconnect()
      mapResizeObserver = null
      cancelAnimationFrame(mapResizeRaf)
      if (fitMiniTimer) { clearTimeout(fitMiniTimer); fitMiniTimer = null }
    })
    // ─────────────────────────────────────────────────────────────────────────

    onMounted(async () => {
      EventBus.on('droneControlMqttInfo', handleDrcMarkerRotation)
      EventBus.on('placeMediaOnMap', onPlaceMediaOnMap)
      const app = getApp()

      // Observe the map container so the canvas always matches its real size.
      const mapContainer = document.getElementById('g-container')
      if (mapContainer && typeof ResizeObserver !== 'undefined') {
        mapResizeObserver = new ResizeObserver(() => {
          cancelAnimationFrame(mapResizeRaf)
          mapResizeRaf = requestAnimationFrame(() => {
            root.$map?.resize()
            // En mode mini, re-cadrer sur les annotations une fois la nouvelle taille appliquée
            if (props.isMini) scheduleFitMini()
          })
        })
        mapResizeObserver.observe(mapContainer)
      }

      // Fetch layers first, then init map so we can render layers inside map.once('load')
      let layersData: any[] | null = null
      try {
        const result = await getElementGroupsReq({ groupId: '' })
        if (result?.data) {
          store.dispatch('setLayerInfo', result.data)
          store.commit('SET_LAYER_INFO', result.data)
          layersData = result.data
        }
      } catch (e) {
        console.warn('[GMap] Failed to init layer groups:', e)
      }
      // Pre-populate snToCallsign from device topo so callsigns are known before first GPS point
      try {
        const wsId = localStorage.getItem(ELocalStorageKey.WorkspaceId) || ''
        const topoRes = await getDeviceTopo(wsId)
        if (topoRes?.code === 0 && Array.isArray(topoRes.data)) {
          topoRes.data.forEach((gateway: any) => {
            const child = gateway.children
            if (child?.device_sn && child?.nickname) {
              store.commit('SET_SN_TO_CALLSIGN', { sn: child.device_sn, callsign: child.nickname })
            }
          })
        }
      } catch (e) { console.warn('[GMap] Failed to pre-populate callsigns from topo:', e) }

      // Set active group for PC users
      if (layersData) {
        if (isPCUser) await setupPCActiveGroup(layersData)
        // Refresh layers
        try {
          const refreshed = await getElementGroupsReq({ groupId: '' })
          if (refreshed?.data) {
            store.dispatch('setLayerInfo', refreshed.data)
            store.commit('SET_LAYER_INFO', refreshed.data)
            EventBus.emit('mapGroupRefresh')
          }
        } catch (_) {}
      }
      useGMapManageHook.globalPropertiesConfig(app, () => {
        // Réinstalle les fonds de carte IGN sauvegardés + réapplique après chaque setStyle
        if (root.$map) initOverlays(root.$map)
        if (layersData) renderLayersOnMap(layersData)
        // Cadre la mini-carte sur toutes les annotations dès qu'elles sont rendues
        scheduleFitMini()
      })
    })

    const { getDrawFlightAreaCallback, onFlightAreaDroneLocationWs } = useFlightArea()
    useFlightAreaDroneLocationEvent(onFlightAreaDroneLocationWs)

    function selectFlightAreaAction ({ type, isCircle }: { type: EFlightAreaType, isCircle: boolean }) {
      draw(isCircle ? MapDoodleEnum.CIRCLE : MapDoodleEnum.POLYGON, true, type)
    }

    function getDrawCallback ({ obj, suggestedRadius, confirm, cancel }: { obj: any, suggestedRadius?: number, confirm?: (r: number) => void, cancel?: () => void }) {
      if (state.isFlightArea) {
        getDrawFlightAreaCallback(obj)
        return
      }
      switch (state.currentType) {
        case MapDoodleEnum.PIN:
          postPinPositionResource(obj)
          break
        case MapDoodleEnum.POLYLINE:
          postPolylineResource(obj)
          break
        case MapDoodleEnum.POLYGON:
          postPolygonResource(obj)
          break
        case MapDoodleEnum.CIRCLE:
          pendingCircleObj.value = { obj, confirm, cancel }
          circleRadius.value = suggestedRadius ?? 100
          circleRadiusVisible.value = true
          break
        default:
          break
      }
    }
    function onCircleRadiusConfirm () {
      circleRadiusVisible.value = false
      if (pendingCircleObj.value) {
        const { obj, confirm } = pendingCircleObj.value
        confirm?.(circleRadius.value)
        postCircleResource(obj, circleRadius.value)
        pendingCircleObj.value = null
      }
    }
    function onCircleRadiusCancel () {
      circleRadiusVisible.value = false
      if (pendingCircleObj.value) {
        pendingCircleObj.value.cancel?.()
        pendingCircleObj.value = null
      }
    }
    async function postPinPositionResource (obj) {
      const req = await getPinPositionResource(obj)
      setLayers(req)
      // Remove the MapboxDraw dot and show the real icon immediately (before the network round-trip)
      obj.remove()
      useGMapCoverHook.init2DPin(req.name, req.resource.content.geometry.coordinates as GeojsonCoordinate, req.resource.content.properties.color, { id: req.id, name: req.name })
      const targetGroupId = annotationTargetGroupId.value
      console.log('Posting new pin to group', targetGroupId, req)
      await postElementsReq(targetGroupId, req)
    }
    async function postPolylineResource (obj) {
      const req = await getPolylineResource(obj)
      setLayers(req)
      obj.remove()
      useGMapCoverHook.initPolyline(req.name, req.resource.content.geometry.coordinates as GeojsonCoordinate[], req.resource.content.properties.color, { id: req.id, name: req.name })
      await postElementsReq(annotationTargetGroupId.value, req)
    }
    async function postPolygonResource (obj) {
      const req = await getPoygonResource(obj)
      setLayers(req)
      obj.remove()
      useGMapCoverHook.initPolygon(req.name, req.resource.content.geometry.coordinates as GeojsonCoordinate[][], req.resource.content.properties.color, { id: req.id, name: req.name })
      await postElementsReq(annotationTargetGroupId.value, req)
    }
    async function postCircleResource (obj, radius: number) {
      const req = await getCircleResource(obj, radius)
      setLayers(req)
      obj.remove()
      const center = req.resource.content.geometry.coordinates as GeojsonCoordinate
      useGMapCoverHook.initCircle(req.name, center, radius, { id: req.id, name: req.name })
      await postElementsReq(annotationTargetGroupId.value, req)
    }
    async function queryElevation (lngLat: { lng: number, lat: number }): Promise<number> {
      const map = root.$map
      if (!map) return 0

      // Try immediately — works if DEM tiles are already loaded at this location
      const immediate = (map as any).queryTerrainElevation(lngLat, { exaggerated: false })
      if (immediate != null) return immediate

      // DEM tiles not yet loaded: retry on each 'idle' event (tiles load between idles).
      // Cap at 8 retries (~8 tile-load cycles) with a 5 s hard timeout.
      return new Promise(resolve => {
        let resolved = false
        const done = (v: number) => {
          if (resolved) return
          resolved = true
          map.off('sourcedata', onSourceData)
          clearTimeout(hardTimeout)
          resolve(v)
        }

        // sourcedata fires when DEM tiles finish loading
        const onSourceData = (e: any) => {
          if (e.sourceId !== 'mapbox-dem') return
          const elev = (map as any).queryTerrainElevation(lngLat, { exaggerated: false })
          if (elev != null) done(elev)
        }
        map.on('sourcedata', onSourceData)

        // Also retry on each idle (covers cases where tiles were already cached)
        let retries = 0
        const onIdle = () => {
          const elev = (map as any).queryTerrainElevation(lngLat, { exaggerated: false })
          if (elev != null) { done(elev); return }
          if (++retries < 8) map.once('idle', onIdle)
        }
        map.once('idle', onIdle)

        // Hard 5 s fallback — return whatever we have (0 if DEM still not loaded)
        const hardTimeout = setTimeout(() => {
          done((map as any).queryTerrainElevation(lngLat, { exaggerated: false }) ?? 0)
        }, 5000)
      })
    }

    // Nom par défaut horodaté à largeur fixe (« Pin 2026-07-14 15:30:22 ») :
    // le tri alphabétique du nom redonne exactement l'ordre de création.
    // Nomme un pin web dans le MÊME style que la RC (« PILOT 10 ») : « <user> <num> », court
    // et sans caractères spéciaux. num = incrément basé sur les pins existants du même user.
    function nextUserPinName (): string {
      const username = localStorage.getItem('username') || 'PC'
      let max = 0
      const scan = (els: any[]) => (els || []).forEach((e: any) => {
        const parts = typeof e?.name === 'string' ? e.name.split(' ') : []
        if (parts.length === 2 && parts[0] === username && /^\d+$/.test(parts[1])) {
          max = Math.max(max, parseInt(parts[1], 10))
        }
      })
      ;(store.state.Layers as any[])?.forEach((l: any) => {
        scan(l.elements)
        ;(l.subGroups || []).forEach((s: any) => scan(s.elements))
      })
      return `${username} ${max + 1}`
    }

    async function getCircleResource (obj, radius: number) {
      const position = obj.getPosition()
      const elevation = toAnnotationAltitude(await queryElevation(position))
      const resource = generateCircleContent(position, radius, elevation)
      const id = uuidv4()
      const name = `circle-${id.slice(0, 6)}`
      return { id, name, resource }
    }

    async function getPinPositionResource (obj) {
      const position = obj.getPosition()
      // Z = altitude du sol en WGS84 ellipsoïdal (= terrain MSL + géoïde) : c'est la référence
      // qu'attend la RC/Pilot pour poser le pin au niveau du sol. Le web, lui, affiche
      // l'altitude de son propre terrain pour les pins (indépendant du Z stocké) → pas d'impact.
      const elevation = toAnnotationAltitude(await queryElevation(position))
      const resource = generatePointContent(position, elevation)
      const id = uuidv4()
      const name = nextUserPinName()
      return { id, name, resource }
    }
    async function getPolylineResource (obj) {
      const path = obj.getPath()
      const elevations = await Promise.all(path.map((pt: any) => queryElevation({ lng: pt.lng, lat: pt.lat })))
      const resource = generateLineContent(path, elevations.map(toAnnotationAltitude))
      const id = uuidv4()
      const name = `line-${id.slice(0, 6)}`
      return { id, name, resource }
    }
    async function getPoygonResource (obj) {
      const path = obj.getPath()
      const elevations = await Promise.all(path.map((pt: any) => queryElevation({ lng: pt.lng, lat: pt.lat })))
      const resource = generatePolyContent(path, elevations.map(toAnnotationAltitude))
      const id = uuidv4()
      const name = `polygon-${id.slice(0, 6)}`
      return { id, name, resource }
    }
    function setLayers (resource: PostElementsBody) {
      const layers = store.state.Layers
      const targetId = annotationTargetGroupId.value
      const layer = layers.find((item: any) => item.id === targetId) ||
        layers.find((item: any) => item.id === shareId.value)
      if (layer?.elements) {
        ;(layer?.elements as any[]).push(resource)
      }
      store.commit('SET_LAYER_INFO', layers)
    }

    // ── Photo placée sur la carte (déclenché depuis le panneau Média) ──────────
    // Une pin PROVISOIRE est déposée à la position de prise de vue, surélevée à
    // l'altitude du drone au déclenchement. L'utilisateur confirme (« Sauvegarder »
    // → persistée comme annotation) ou annule (« Fermer » → rien).
    const mediaPinDraft = ref<null | {
      name: string
      coordinates: GeojsonCoordinate // [lng, lat, z]
      height: number // hauteur au-dessus du sol (m)
      clampToGround: boolean
      marker: mapboxgl.Marker
    }>(null)
    const mediaPinSaving = ref(false)

    async function onPlaceMediaOnMap (payload: { name: string, lat: number, lng: number, absoluteAltitude: number | null, relativeAltitude: number | null }) {
      const map = root.$map as any
      if (!map) return
      removeMediaPinDraft()

      const { lat, lng, name } = payload
      // Terrain en MSL brut (même référence que l'éditeur d'annotations / deriveElevation).
      const terrainMsl = await queryElevation({ lng, lat })
      const abs = payload.absoluteAltitude
      const rel = payload.relativeAltitude

      // Hauteur AGL + altitude Z stockée. On veut la VRAIE altitude du drone (ASL), pas la
      // hauteur relative au décollage (fausse si le drone n'a pas décollé du sol survolé).
      //  - priorité à l'altitude ABSOLUE DJI, corrigée du géoïde : DJI la donne en WGS84
      //    ellipsoïdal, le terrain Mapbox est en MSL orthométrique → on retire geoidOffset
      //    (WGS84 − MSL, ≈47 m en France) pour ramener les deux dans la même référence.
      //  - repli si le géoïde n'est pas encore calibré (aucun atterrissage cette session) :
      //    altitude relative (au-dessus du décollage), insensible au géoïde.
      const geoid = geoidOffset ?? store.state.geoidOffset ?? null
      let height: number
      let z: number
      if (abs != null && isFinite(abs) && geoid != null) {
        z = abs - geoid // WGS84 ellipsoïdal → MSL orthométrique
        height = Math.max(0, Math.round(z - terrainMsl))
      } else if (rel != null && isFinite(rel)) {
        height = Math.max(0, Math.round(rel))
        z = terrainMsl + height
      } else if (abs != null && isFinite(abs)) {
        // Dernier repli : absolue non corrigée (peut être ~geoidOffset trop haute).
        z = abs
        height = Math.max(0, Math.round(abs - terrainMsl))
      } else {
        height = 0
        z = terrainMsl
      }
      const clampToGround = height <= 0
      const coordinates = [lng, lat, z] as GeojsonCoordinate

      // Marqueur provisoire — même rendu qu'une pin sauvegardée (init2DPin, altitude = hauteur AGL),
      // teinté pour signaler qu'il n'est pas encore enregistré.
      const el = document.createElement('img')
      el.src = pin
      el.style.width = '30px'
      el.style.height = '40px'
      el.style.filter = 'hue-rotate(140deg) saturate(1.6)'
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' }).setLngLat([lng, lat])
      if (!clampToGround) (marker as any).setAltitude?.(height)
      marker.addTo(map)

      mediaPinDraft.value = { name, coordinates, height, clampToGround, marker }
      map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom?.() ?? 16, 16) })
    }

    function removeMediaPinDraft () {
      mediaPinDraft.value?.marker.remove()
      mediaPinDraft.value = null
    }

    async function saveMediaPin () {
      const draft = mediaPinDraft.value
      if (!draft || mediaPinSaving.value) return
      mediaPinSaving.value = true
      try {
        const id = uuidv4()
        const resource = {
          type: MapElementEnum.PIN,
          content: generatePoint(
            { height: draft.coordinates[2] as number, latitude: draft.coordinates[1], longitude: draft.coordinates[0] },
            { color: MapDoodleColor.PinColor, clampToGround: draft.clampToGround, height: draft.height } as any
          ),
        }
        const req = { id, name: draft.name, resource } as any
        setLayers(req)
        await postElementsReq(annotationTargetGroupId.value, req)
        // Rendu permanent : init2DPin place la pin à sa hauteur AGL (mode agl).
        useGMapCoverHook.init2DPin(draft.name, draft.coordinates, MapDoodleColor.PinColor, { id, name: draft.name }, draft.height, { mode: 'agl' })
        removeMediaPinDraft()
        message.success('Photo ajoutée à la carte')
        EventBus.emit('mapGroupRefresh')
      } catch (e) {
        message.error('Échec de l\'enregistrement de la pin')
      } finally {
        mediaPinSaving.value = false
      }
    }

    const swapped = computed(() => store.state.livestreamSwapped)

    // ── Vue 3D : bascule de l'inclinaison caméra + réglage du relief ──────────
    const is3D = ref(false)
    const terrainExag = ref(getTerrainExaggeration())
    function toggle3D () {
      const map = root.$map
      if (!map) return
      is3D.value = !is3D.value
      map.easeTo({ pitch: is3D.value ? 70 : 0, duration: 800 })
    }
    function onExagChange (val: number) {
      const map = root.$map
      if (map) setTerrainExaggeration(map, val)
    }

    // Terrain haute résolution IGN (RGE ALTI / LiDAR HD) vs DEM Mapbox
    const terrainIgn = ref(getTerrainSource() === 'ign')
    function toggleTerrainIgn () {
      const map = root.$map
      if (!map) return
      terrainIgn.value = !terrainIgn.value
      setTerrainSource(map, terrainIgn.value ? 'ign' : 'mapbox')
      // Le relief ne se voit qu'en 3D → on bascule en 3D à l'activation pour le rendre visible
      if (terrainIgn.value && !is3D.value) toggle3D()
    }

    // Bâtiments 3D (extrusions)
    const buildings3D = ref(getBuildingsVisible())
    function toggleBuildings3D () {
      const map = root.$map
      if (!map) return
      buildings3D.value = !buildings3D.value
      setBuildingsVisible(map, buildings3D.value)
      // Les bâtiments ne se voient qu'en 3D → on bascule en 3D à l'activation
      if (buildings3D.value && !is3D.value) toggle3D()
    }
    // ─────────────────────────────────────────────────────────────────────────

    const currentMapStyle = ref('satellite-streets-v12')
    function changeMapStyle (styleId: string) {
      const map = root.$map
      if (!map) return
      currentMapStyle.value = styleId

      // Remove all custom elements before style change (markers are DOM elements that survive setStyle,
      // but sources/layers are wiped — clean everything for a clean re-render)
      const coverMap = store.state.coverMap
      Object.values(coverMap).forEach((items: any[]) => {
        items.forEach((item: any) => item.remove?.())
      })
      Object.keys(coverMap).forEach(key => delete coverMap[key])

      map.setStyle(`mapbox://styles/mapbox/${styleId}`)
      map.once('style.load', () => {
        store.commit('TRIGGER_LAYER_REDRAW')
      })
    }
    return {
      draw,
      mouseMode,
      circleRadiusVisible,
      circleRadius,
      onCircleRadiusConfirm,
      onCircleRadiusCancel,
      drawVisible,
      osdVisible,
      pin,
      state,
      M30,
      Mavic3E,
      deviceInfo,
      applyGeoidCorrection,
      EGear,
      EModeCode,
      str,
      EDockModeCode,
      dockControlPanelVisible,
      setDockControlPanelVisible,
      onCloseControlPanel,
      NetworkStateTypeEnum,
      NetworkStateQualityEnum,
      RainfallEnum,
      DroneInDockEnum,
      osdPositionStyle,
      qualityStyle,
      selectFlightAreaAction,
      swapped,
      currentMapStyle,
      changeMapStyle,
      canAddAnnotations,
      is3D,
      terrainExag,
      toggle3D,
      onExagChange,
      terrainIgn,
      toggleTerrainIgn,
      buildings3D,
      toggleBuildings3D,
      formatWindSpeed,
      windUnitLabel,
      toggleWindUnit: toggleWindSpeedUnit,
      mediaPinDraft,
      mediaPinSaving,
      saveMediaPin,
      removeMediaPinDraft,
    }
  }
})
</script>

<style lang="scss" scoped>

.g-map-wrapper {
  height: 100%;
  width: 100%;
  position: relative; // needed for absolute OSD panels

  .map-canvas-wrapper {
    position: relative; // sélecteur de style ancré ici

    .draw-hint-banner {
      position: absolute;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 20;
      background: rgba(0, 0, 0, 0.75);
      color: #fff;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      white-space: nowrap;
      pointer-events: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    }

    // Barre de confirmation « photo sur la carte » (au-dessus, centrée)
    .media-pin-confirm {
      position: absolute;
      top: 16px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 25;
      display: flex;
      align-items: center;
      gap: 10px;
      background: rgba(0, 0, 0, 0.82);
      color: #fff;
      padding: 8px 12px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);

      &__label {
        display: flex;
        flex-direction: column;
        line-height: 1.25;
        max-width: 240px;
      }
      &__name {
        font-size: 13px;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      &__alt {
        font-size: 11px;
        opacity: 0.75;
      }
      &__btn {
        border: none;
        border-radius: 5px;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        background: rgba(255, 255, 255, 0.14);
        color: #fff;
        transition: background 0.15s;

        &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.26); }
        &:disabled { opacity: 0.55; cursor: default; }
      }
      &__btn--save {
        background: #2d8cf0;
        &:hover:not(:disabled) { background: #1f7ad9; }
      }
    }

    .map-style-switcher {
      position: absolute;
      bottom: 32px;
      left: 10px;
      z-index: 10;
      display: flex;
      align-items: center;

      .terrain-exag {
        display: flex;
        align-items: center;
        margin-left: 8px;
        padding: 0 8px;
        height: 24px;
        background: rgba(0, 0, 0, 0.6);
        border-radius: 4px;

        .terrain-exag-label {
          color: #fff;
          font-size: 11px;
          white-space: nowrap;
        }
      }
    }

  }

  .g-action-panel {
    position: absolute;
    top: 16px;
    right: 16px;
    .g-action-item {
      width: 28px;
      height: 28px;
      background: white;
      color: $primary;
      border-radius: 2px;
      line-height: 28px;
      text-align: center;
      margin-bottom: 2px;
    }
    .g-action-item:hover {
      border: 1px solid $primary;
      border-radius: 2px;
    }
  }
  .selection {
    border: 1px solid $primary;
    border-radius: 2px;
  }

  // antd button 光晕
  &:deep(.ant-btn){
    &::after {
      display: none;
    }
  }
}
.osd-panel {
  position: absolute;
  width: 480px;
  background: #000;
  color: #fff;
  border-radius: 2px;
  opacity: 0.8;
  z-index: 2;
}
.osd > div:not(.dock-control-panel) {
  margin-top: 5px;
  padding-left: 5px;
}

.circle {
  border-radius: 50%;
  width: 10px;
  height: 10px;
}

.battery-slide {
  .capacity-percent {
    background: #00ee8b;
  }
  .return-home {
    background: #ff9f0a;
  }
  .landing {
    background: #f5222d;
  }
  .white-point {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: white;
    bottom: -0.5px;
  }
  .battery {
    background: #141414;
    color: #00ee8b;
    margin-top: -10px;
    height: 20px;
    width: auto;
    border-left: 1px solid #00ee8b;
    padding: 0 5px;
  }
}
.battery-slide > div {
  position: absolute;
  min-height: 2px;
  border-radius: 2px;
}

</style>
